<script lang="ts">
	import { formatCurrency } from "@fergmar/ui/formatters";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import BedDoubleIcon from "@lucide/svelte/icons/bed-double";
	import BathIcon from "@lucide/svelte/icons/bath";
	import LandPlotIcon from "@lucide/svelte/icons/land-plot";
	import CalendarIcon from "@lucide/svelte/icons/calendar";
	import ImageIcon from "@lucide/svelte/icons/image";
	import type { OnMarketUnitEntity } from "@fergmar/contracts/public/buildium-listings";

	let {
		listing
	}: {
		listing: OnMarketUnitEntity;
	} = $props();

	const cardLink = $derived(`/properties/for-rent/${listing.listing.slug}`);
	const price = $derived(listing.listing.rent);
	const deposit = $derived(listing.listing.deposit ?? undefined);
	const addressLine1 = $derived(listing.unit.address.addressLine1);
	const addressLine2 = $derived(listing.unit.address.addressLine2);
	const city = $derived(listing.unit.address.city);
	const usState = $derived(listing.unit.address.state);
	const postalCode = $derived(listing.unit.address.postalCode);
	const beds = $derived(listing.unit.unitBedrooms?.count ?? 0);
	const baths = $derived(listing.unit.unitBathrooms?.count ?? 0);
	const size = $derived(listing.unit.unitSize);
	const sizeUnit = "sf" as const;
	const availableDate = $derived(
		new Date(listing.listing.availableDate ?? "").toLocaleDateString()
	);

	const featuredImage = $derived(listing.unit.files?.[0]);
	const imageUrl = $derived(
		cloudinaryFetch(featuredImage?.url, { width: 400, height: 300, crop: "fill" })
	);

	let imageError = $state(false);

	function handleImageError() {
		imageError = true;
	}
</script>

<a
	href={cardLink}
	class="hover:no-underline"
	data-sveltekit-preload-data="tap"
	data-sveltekit-preload-code="hover"
>
	<div class="flex flex-col rounded border">
		<div
			class="relative h-56 w-full overflow-hidden rounded-t shadow-xl sm:h-48 md:h-52 lg:h-48"
		>
			{#if imageUrl && !imageError}
				<!-- Actual image with size optimization hints -->
				<img
					src={imageUrl}
					alt={`${listing.unit.address.addressLine1} ${listing.unit.address.addressLine2} ${listing.unit.address.city} ${listing.unit.address.state} ${listing.unit.address.postalCode} Featured Image`}
					class="h-full w-full object-cover object-center"
					loading="lazy"
					decoding="async"
					width="400"
					height="300"
					onerror={handleImageError}
				/>
			{:else}
				<!-- No image or error fallback -->
				<div class="flex h-full w-full items-center justify-center bg-gray-100">
					<div class="flex flex-col items-center text-center">
						<ImageIcon class="mb-2 h-8 w-8 text-gray-400" />
						<span class="text-sm font-medium text-gray-500">
							{imageError ? "Image unavailable" : "No Property Images"}
						</span>
					</div>
				</div>
			{/if}
		</div>
		<div class="p-4">
			<!-- Rent -->
			<div class="mb-2 flex items-center justify-between">
				<span class="text-lg font-bold">
					{formatCurrency(price)}/mo
				</span>
				{#if deposit && deposit > 0}
					<span class="text-muted-foreground text-sm">
						{formatCurrency(deposit)} deposit
					</span>
				{/if}
			</div>

			<!-- Address Info -->
			<div class="flex flex-col items-start">
				<div class="mb-1">
					<span class="font-semibold">{addressLine1}</span>
					{#if addressLine2}
						<span class="text-border ml-1">|</span><span class="text-primary ml-1"
							>{addressLine2}</span
						>
					{/if}
				</div>
				<span class="text-foreground/70 text-sm">
					{city}, {usState}
					{postalCode}
				</span>
			</div>

			<hr class="bg-border my-3" />

			<!-- Unit Details -->
			<div class="text-muted-foreground mt-2 flex flex-row gap-3 text-sm">
				{#if beds !== undefined && beds > 0}
					<div class="flex flex-row items-center gap-1">
						<BedDoubleIcon class="h-4 w-4" />
						<span>{beds} {beds === 1 ? "Bed" : "Beds"}</span>
					</div>
				{/if}
				{#if baths !== undefined && baths > 0}
					<div class="flex flex-row items-center gap-1">
						<BathIcon class="h-4 w-4" />
						<span>{baths} {baths === 1 ? "Bath" : "Baths"}</span>
					</div>
				{/if}
				{#if size !== undefined && size > 0}
					<div class="flex flex-row items-center gap-1">
						<LandPlotIcon class="h-4 w-4" />
						<span>{size.toLocaleString()} {sizeUnit}</span>
					</div>
				{/if}
			</div>

			<!-- Available Date -->
			{#if availableDate}
				<div class="text-muted-foreground mt-3 flex items-center text-sm">
					<CalendarIcon class="mr-1 h-4 w-4" />
					<span>Available {availableDate}</span>
				</div>
			{/if}
		</div>
	</div>
</a>
